import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import clsx from 'clsx';
import dayjs from 'dayjs';

import { useActivities, useUserStreaks } from 'src/services/api';

import styles from 'src/components/layout/landlord/ActivityCalendar.module.css';

const WEEKDAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const STREAK_INFO_TOOLTIP = 'Manual actions such as sending a message, creating a tour, or running a playbook count towards your daily activity.';

export default function ActivityCalendar() {
    const navigate = useNavigate();
    const { data: activities } = useActivities();
    const { data: streak } = useUserStreaks();

    const completion = useMemo(() => {
        const fallback = Array(WEEKDAY_LABELS.length).fill(false);
        if (!activities) {
            return fallback;
        }

        // UI is Monday-first (M ... S). Build completion for current week.
        const today = dayjs().startOf('day');
        const mondayOffset = (today.day() + 6) % 7;
        const startOfWeek = today.subtract(mondayOffset, 'day');
        const endOfWeek = startOfWeek.add(6, 'day').endOf('day');
        const apiCompletion = Array(WEEKDAY_LABELS.length).fill(false);

        activities.forEach((activity) => {
            const activityDay = dayjs(activity.created_at);
            if (activityDay.isBefore(startOfWeek) || activityDay.isAfter(endOfWeek)) {
                return;
            }

            const mondayFirstDayIndex = (activityDay.day() + 6) % 7;
            apiCompletion[mondayFirstDayIndex] = true;
        });

        return apiCompletion;
    }, [activities]);
    const streakWeeks = streak?.week || 0;

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
            >
                <Box display="flex" alignItems="center" gap={0.75}>
                    <BoltRoundedIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                        Streak
                    </Typography>
                    <Tooltip title={STREAK_INFO_TOOLTIP}>
                        {/* <IconButton size="small" sx={{ p: 0.25 }}> */}
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        {/* </IconButton> */}
                    </Tooltip>
                </Box>
                <IconButton
                    size="small"
                    sx={{ p: 0.25 }}
                    onClick={() => navigate('/landlord/inner/activity')}
                >
                    <ChevronRightRoundedIcon sx={{ fontSize: 20, color: 'text.primary' }} />
                </IconButton>
            </Box>

            <Box display="flex" alignItems="baseline" gap={0.5} mb={2}>
                <Typography variant="h5" fontWeight={700} color="text.primary">
                    {streakWeeks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    weeks
                </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={0.5}>
                {WEEKDAY_LABELS.map((day, i) => {
                    const isComplete = completion[i] === true;
                    return (
                        <Box
                            key={`${day}-${i}`}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={0.5}
                        >
                            <Box
                                className={clsx(styles.dayCircle, isComplete ? styles.dayCircleComplete : styles.dayCircleIncomplete)}
                            >
                                {isComplete && (
                                    <CheckRoundedIcon
                                        sx={{ fontSize: 12, color: 'white' }}
                                    />
                                )}
                            </Box>
                            <Typography
                                variant="caption"
                                fontWeight={500}
                                color={isComplete ? 'var(--color-primary-500)' : 'text.secondary'}
                            >
                                {day}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
}
